import Modal from '../../../components/common/modal';
import UseFormDropDown from '../../../components/common/use-form-drop-down';
import { ToggleButton } from '../../../components/common';
import { ClientSummary } from '../../../hooks/useClients';
import { useEffect, useState } from 'react';
import AlertBox from '../../../components/route-leaving-prompt/alert-box';
import { FieldErrors, UseFormGetValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useEmailAddressAvailable } from '../../../hooks/useUsers';
import useDebounce from '../../../components/common/debounce';
import { MemberProfile } from '../../../hooks/useMembers';
import { validateMobilePhone, normalizeMobilePhone } from '../../../components/common/phone-validation';

interface MemberFormData {
  first_name: string;
  last_name: string;
  email_address: string;
  mobile_phone: string;
  timezone: string;
  gender: string;
  pronoun: string;
  date_of_birth: string;
  race: string;
  is_text_compatible_phone: boolean;
  caller_role: {
    active_subscription: {
      package: {
        client: {
          name: string;
        };
        name: string;
      };
    };
    listener_preferences: {
      languages: string[];
    };
  };
}

interface EditDetailsProps {
  showEditModal: boolean;
  onClose: () => void;
  onSave: () => void;
  tags: {
    genders: string[];
    pronouns: string[];
    races: string[];
  };
  timeZones: string[];
  clients: ClientSummary[];
  packages: Array<{ name: string; code: string }>;
  language: string[];
  selectLanguage: (language: string) => void;
  setPackages: (packages: Array<{ name: string; code: string }>) => void;
  setSelectedPackage: (selectedPackage: { name: string; code: string }) => void;
  setIsModified: (modified: boolean) => void;
  register: UseFormRegister<MemberProfile>;
  getValues: UseFormGetValues<MemberProfile>;
  setValue: UseFormSetValue<MemberProfile>;
  errors: FieldErrors<MemberProfile>;
  isTextCompatiblePhone: boolean;
  setIsTextCompatiblePhone: (value: boolean) => void;
}

export const EditDetails: React.FC<EditDetailsProps> = ({
  showEditModal,
  onClose,
  onSave,
  tags,
  timeZones,
  clients,
  packages,
  language,
  selectLanguage,
  setPackages,
  setSelectedPackage,
  setIsModified,
  register,
  getValues,
  setValue,
  errors,
  isTextCompatiblePhone,
  setIsTextCompatiblePhone,
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [formValues, setFormValues] = useState<Partial<MemberFormData> | null>(null);
  const [email, setEmail] = useState<string>(getValues('email_address'));
  const debouncedEmail = useDebounce(email, 500);
  const { data: emailAvailable } = useEmailAddressAvailable(debouncedEmail);

  const handleCloseModal = () => {
    if (hasUnsavedChanges) {
      setShowAlert(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    if (formValues) {
      (Object.entries(formValues) as Array<[keyof MemberFormData, any]>).forEach(([key, value]) => {
        if (value !== undefined) {
          setValue(key, value);
        }
      });
    }
    setHasUnsavedChanges(false);
    setShowAlert(false);
    onClose();
  };

  const handleFormChange = () => {
    setIsModified(true);
    setHasUnsavedChanges(true);
  };

  return (
    <>
      <Modal
        isModel={showEditModal}
        InnerComponent={
          <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="mb-4 text-xl font-bold leading-6">Edit Member Details</div>
              <div className="text-md mb-4 border-b border-gray-200 pb-2 font-bold leading-6">Plan Details</div>
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div className="w-full px-2">
                  <UseFormDropDown label={'Group'}>
                    <select
                      data-testid="group-select"
                      className="select-box w-full focus:ring-transparent"
                      {...register('caller_role.active_subscription.package.client.name', {
                        onChange: (e) => {
                          const find = clients?.length
                            ? clients.find((item: any) => item.name === e.target.value)
                            : null;
                          if (find && find?.packages?.length) {
                            setPackages(find.packages);
                            setSelectedPackage(find.packages[0]);
                          }
                          handleFormChange();
                        },
                      })}
                    >
                      <option value="">Select group</option>
                      {clients?.map((item: any) => (
                        <option key={item.name} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </UseFormDropDown>
                </div>
                <div className="w-full px-2">
                  <UseFormDropDown label={'Plan'}>
                    <select
                      data-testid="plan-select"
                      className="select-box w-full focus:ring-transparent"
                      {...register('caller_role.active_subscription.package.name', {
                        onChange: (e) => {
                          const selectedPackage = packages.find((p) => p.name === e.target.value);
                          if (selectedPackage) {
                            setSelectedPackage(selectedPackage);
                          }
                          handleFormChange();
                        },
                      })}
                    >
                      {packages?.length > 0 &&
                        packages.map((item: any) => (
                          <option key={item.name} value={item.name}>
                            {item.name}
                          </option>
                        ))}
                    </select>
                  </UseFormDropDown>
                </div>
              </div>
              <div className="text-md mb-4 border-b border-gray-200 pb-2 font-bold leading-6">Contact</div>
              <div className="mb-4 grid grid-cols-3 gap-4">
                <div className="w-full px-2">
                  <label className="input-label">First Name</label>
                  <input
                    data-testid="first-name-input"
                    {...register('first_name', {
                      onBlur: (e: any) => setValue('first_name', e.target.value.trim()),
                      onChange: () => handleFormChange(),
                    })}
                    className="input focus:ring-transparent"
                  />
                </div>
                <div className="w-full px-2">
                  <label className="input-label">Last Name</label>
                  <input
                    data-testid="last-name-input"
                    {...register('last_name', {
                      onBlur: (e: any) => setValue('last_name', e.target.value.trim()),
                      onChange: () => handleFormChange(),
                    })}
                    className="input focus:ring-transparent"
                  />
                </div>
                <div className="w-full px-2">
                  <UseFormDropDown label={'Time Zone'}>
                    <select
                      data-testid="timezone-select"
                      className="select-box w-full focus:ring-transparent"
                      {...register('timezone', {
                        onChange: () => handleFormChange(),
                      })}
                    >
                      <option value="">Select time zone</option>
                      {timeZones.map((item: any) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </UseFormDropDown>
                </div>
                <div className="w-full px-2">
                  <label className="input-label">Email</label>
                  <input
                    data-testid="email-input"
                    {...register('email_address', {
                      required: 'Enter email address',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Enter valid email address',
                      },
                      validate: (value) => {
                        if (value !== formValues?.email_address && emailAvailable === false) return false;
                      },
                      onChange: (e) => {
                        setEmail(e.target.value);
                        handleFormChange();
                      },
                      onBlur: (e: any) => {
                        const trimmedValue = e.target.value.trim();
                        setValue('email_address', trimmedValue, { shouldValidate: true });
                      },
                    })}
                    className="input focus:ring-transparent"
                  />
                  {errors.email_address?.message && (
                    <label className="input-error">{errors.email_address.message}</label>
                  )}
                  {errors.email_address &&
                    // @ts-ignore
                    errors.email_address.type === 'validate' && (
                      <div className="input-error">Email already used, use different email.</div>
                    )}
                </div>
                <div className="w-full px-2">
                  <label className="input-label">Phone</label>
                  <input
                    data-testid="phone-input"
                    {...register('mobile_phone', {
                      validate: async (value) => {
                        if (value.trim() === '') return true;
                        const phoneErrorMessage = await validateMobilePhone(value);
                        if (phoneErrorMessage !== '') {
                          return phoneErrorMessage;
                        }
                        return true;
                      },
                      onChange: () => {
                        handleFormChange();
                      },
                      onBlur: (e: any) => {
                        const mobilePhoneInput = e.target.value.replace('+1', '');
                        const normalized = normalizeMobilePhone(mobilePhoneInput);
                        setValue('mobile_phone', normalized, { shouldValidate: true });
                      },
                    })}
                    className="input focus:ring-transparent"
                  />
                  {errors.mobile_phone?.message ? (
                    <label className="input-error">{errors.mobile_phone?.message}</label>
                  ) : errors.mobile_phone &&
                    // @ts-ignore
                    errors?.mobile_phone?.type === 'validate' ? (
                    <div className="input-error">Enter valid phone number</div>
                  ) : null}
                </div>
                <div className="w-full">
                  <div className="flex min-w-48 flex-col gap-2">
                    <label className="text-gray-dark block leading-4">Receive Text Comms</label>
                    <ToggleButton
                      data-testid="text-comms-toggle"
                      id={'is_text_compatible_phone'}
                      status={isTextCompatiblePhone}
                      setStatus={(data) => {
                        setIsTextCompatiblePhone(data);
                        setValue('is_text_compatible_phone', data);
                        handleFormChange();
                      }}
                      initialstatus={true}
                    />
                  </div>
                </div>
              </div>
              <div className="text-md mb-4 border-b border-gray-200 pb-2 font-bold leading-6">
                Matching Demographics
              </div>
              <div className="mb-4 grid grid-cols-3 gap-4">
                <div className="mb-4">
                  <UseFormDropDown label={'Gender'}>
                    <select
                      data-testid="gender-select"
                      className="select-box w-full focus:ring-transparent"
                      {...register('gender', {
                        onChange: () => handleFormChange(),
                      })}
                    >
                      {tags.genders?.map((item: string) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </UseFormDropDown>
                </div>
                <div className="mb-4">
                  <UseFormDropDown label={'Pronouns'}>
                    <select
                      data-testid="pronouns-select"
                      className="select-box w-full focus:ring-transparent"
                      {...register('pronoun', {
                        onChange: () => handleFormChange(),
                      })}
                    >
                      <option value="">Select pronoun</option>
                      {tags.pronouns?.map((item: any) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </UseFormDropDown>
                </div>
                <div className="mb-4">
                  <label className="input-label">DOB (Age)</label>
                  <input
                    data-testid="dob-input"
                    {...register('date_of_birth', {
                      onChange: () => handleFormChange(),
                    })}
                    type="date"
                    className="input focus:ring-transparent"
                  />
                </div>
                <div className="mb-4">
                  <UseFormDropDown label={'Race'}>
                    <select
                      data-testid="race-select"
                      className="select-box w-full focus:ring-transparent"
                      {...register('race', {
                        onChange: () => handleFormChange(),
                      })}
                    >
                      <option value="">Select race</option>
                      {tags.races?.map((item: any) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </UseFormDropDown>
                </div>
                <div className="col-span-2 mb-4">
                  <div className="flex w-full flex-col gap-1">
                    <div className="input-label">Preferred Language</div>
                    <div className="flex w-full gap-4">
                      <div className="flex">
                        <label className="text-gray-dark flex items-center pr-4 leading-4"> English </label>
                        <ToggleButton
                          data-testid="english-toggle"
                          id={'english'}
                          status={language && language.includes('english')}
                          setStatus={(data) => {
                            selectLanguage('english');
                            handleFormChange();
                          }}
                          initialstatus={true}
                        ></ToggleButton>
                      </div>
                      <div className="flex">
                        <label className="text-gray-dark flex items-center pr-4 leading-4"> Spanish </label>
                        <ToggleButton
                          data-testid="spanish-toggle"
                          id={'spanish'}
                          status={language && language.includes('spanish')}
                          setStatus={(data) => {
                            selectLanguage('spanish');
                            handleFormChange();
                          }}
                          initialstatus={true}
                        ></ToggleButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-3">
              <button
                data-testid="profile-edit-cancel-button"
                type="button"
                className="bg-red hover:bg-red/80 flex h-10 w-36 items-center justify-center rounded-sm px-2 text-sm text-white"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                data-testid="save-changes-button"
                type="button"
                className={`flex h-10 w-36 items-center justify-center rounded-sm px-2 text-sm text-white ${
                  !hasUnsavedChanges || Object.keys(errors).length > 0
                    ? 'cursor-not-allowed bg-gray-400'
                    : 'bg-blue-primary hover:bg-blue-primary-dark'
                }`}
                onClick={onSave}
                disabled={!hasUnsavedChanges || Object.keys(errors).length > 0}
              >
                Save Changes
              </button>
            </div>
          </div>
        }
      />
      <AlertBox
        visible={showAlert}
        handlePrompt={handleDiscardChanges}
        handleContinueSave={onSave}
        closeModal={() => setShowAlert(false)}
        confirmButtonText={'Discard Changes'}
        titleText={'Alert'}
        contentText={'You have unsaved changes. If you leave this screen without saving, your changes will be lost.'}
        confirmSaveButtonText={'Save Changes'}
        cancelButtonText={'Cancel'}
      />
    </>
  );
};
